import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "ld_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Public (unauthenticated) client
const pub = axios.create({ baseURL: API });

// Authenticated client
export const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ----- Public lead / forms -----
export const submitLead = (data) => pub.post("/leads", data).then((r) => r.data);
export const checkEligibility = (data) => pub.post("/eligibility", data).then((r) => r.data);
export const applyCareer = (data) => pub.post("/careers", data).then((r) => r.data);
export const submitContact = (data) => pub.post("/contact", data).then((r) => r.data);
export const getContent = (type) => pub.get("/content", { params: type ? { type } : {} }).then((r) => r.data);
export const submitFranchise = (data) => pub.post("/franchise", data).then((r) => r.data);
export const submitInvestor = (data) => pub.post("/investor", data).then((r) => r.data);

// ----- Auth -----
export const apiLogin = (data) => pub.post("/auth/login", data).then((r) => r.data);
export const apiRegister = (data) => pub.post("/auth/register", data).then((r) => r.data);
export const apiMe = () => http.get("/auth/me").then((r) => r.data);
export const apiGoogleSession = (sessionId) =>
  pub.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data);

// ----- Payouts -----
export const requestPayout = (data) => http.post("/payouts/request", data).then((r) => r.data);
export const myPayouts = () => http.get("/payouts/mine").then((r) => r.data);
export const adminPayouts = () => http.get("/admin/payouts").then((r) => r.data);
export const adminUpdatePayout = (id, data) => http.patch(`/admin/payouts/${id}`, data).then((r) => r.data);

// ----- Team tree -----
export const teamTree = () => http.get("/team/tree").then((r) => r.data);

// ----- Remote document upload link -----
export const getUploadLink = () => http.get("/documents/upload-link").then((r) => r.data);
export const uploadPortalInfo = (token) => pub.get(`/u/${token}/info`).then((r) => r.data);
export const uploadPortalFile = (token, formData) =>
  pub.post(`/u/${token}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);

// ----- Customer -----
export const createApplication = (data) => http.post("/applications", data).then((r) => r.data);
export const myApplications = () => http.get("/applications/mine").then((r) => r.data);
export const myDocuments = () => http.get("/documents/mine").then((r) => r.data);
export const uploadDocument = (formData) =>
  http.post("/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const fileUrl = (path) => `${API}/files/${path}`;

// ----- Agent -----
export const agentAddClient = (data) => http.post("/agent/clients", data).then((r) => r.data);
export const agentApplications = () => http.get("/agent/applications").then((r) => r.data);
export const agentCommissions = () => http.get("/agent/commissions").then((r) => r.data);
export const agentTeam = () => http.get("/agent/team").then((r) => r.data);

// ----- Affiliate -----
export const affiliateStats = () => http.get("/affiliate/stats").then((r) => r.data);

// ----- Admin -----
export const adminStats = () => http.get("/admin/stats").then((r) => r.data);
export const adminLeads = () => http.get("/admin/leads").then((r) => r.data);
export const adminUpdateLead = (id, data) => http.patch(`/admin/leads/${id}`, data).then((r) => r.data);
export const adminApplications = () => http.get("/admin/applications").then((r) => r.data);
export const adminUpdateApplication = (id, data) => http.patch(`/admin/applications/${id}`, data).then((r) => r.data);
export const adminUsers = () => http.get("/admin/users").then((r) => r.data);
export const adminCreateUser = (data) => http.post("/admin/users", data).then((r) => r.data);
export const adminCommissions = () => http.get("/admin/commissions").then((r) => r.data);
export const adminUpdateCommission = (id, data) => http.patch(`/admin/commissions/${id}`, data).then((r) => r.data);
export const adminChats = () => http.get("/admin/chats").then((r) => r.data);
export const adminStaff = () => http.get("/admin/staff").then((r) => r.data);
export const adminUpdateChat = (sid, data) => http.patch(`/admin/chats/${sid}`, data).then((r) => r.data);
export const adminAddChatNote = (sid, note) => http.post(`/admin/chats/${sid}/note`, { note }).then((r) => r.data);
export const listRanks = () => http.get("/ranks").then((r) => r.data);
export const adminRanks = () => http.get("/admin/ranks").then((r) => r.data);
export const adminCreateRank = (data) => http.post("/admin/ranks", data).then((r) => r.data);
export const adminUpdateRank = (id, data) => http.put(`/admin/ranks/${id}`, data).then((r) => r.data);
export const adminDeleteRank = (id) => http.delete(`/admin/ranks/${id}`).then((r) => r.data);
export const adminRecomputeRanks = () => http.post("/admin/ranks/recompute").then((r) => r.data);
export const rankMine = () => http.get("/rank/mine").then((r) => r.data);
export const adminIntegrations = () => http.get("/admin/integrations").then((r) => r.data);
export const getNotifications = () => http.get("/notifications").then((r) => r.data);
export const markNotificationsRead = () => http.post("/notifications/read").then((r) => r.data);
export const resolveNotification = (entity_type, entity_id) => http.get("/notifications/resolve", { params: { entity_type, entity_id } }).then((r) => r.data);
export const adminOperations = () => http.get("/admin/operations").then((r) => r.data);
export const adminDocuments = () => http.get("/admin/documents").then((r) => r.data);
export const adminReviewDocument = (id) => http.patch(`/admin/documents/${id}/review`).then((r) => r.data);
export const publicRanks = () => http.get("/public/ranks").then((r) => r.data);
export const adminGetSettings = () => http.get("/admin/settings").then((r) => r.data);
export const adminSetSettings = (data) => http.put("/admin/settings", data).then((r) => r.data);
export const adminContentList = () => http.get("/admin/content").then((r) => r.data);
export const adminContentCreate = (data) => http.post("/admin/content", data).then((r) => r.data);
export const adminContentUpdate = (id, data) => http.put(`/admin/content/${id}`, data).then((r) => r.data);
export const adminContentDelete = (id) => http.delete(`/admin/content/${id}`).then((r) => r.data);
